<template>
  <div ref="root" class="cf-jump">
    <button
      type="button"
      class="cf-jump__btn"
      :class="{ 'cf-jump__btn--open': open }"
      :aria-expanded="open"
      data-cy="cf-jump-toggle"
      @click="open = !open"
    >
      <i class="fas fa-list-ul"></i>
      Jump to
      <i class="fas fa-chevron-down cf-jump__caret"></i>
    </button>

    <Transition name="cf-jump-panel">
      <div
        v-if="open"
        class="cf-jump__panel"
        :class="{ 'cf-jump__panel--right': align === 'right' }"
        data-cy="cf-jump-panel"
      >
        <CustomFormOutlineList
          :sections="sections"
          @jump-section="emitJump('jump-section', $event)"
          @jump-question="emitJump('jump-question', $event)"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

import CustomFormOutlineList, {
  type OutlineSection
} from "./CustomFormOutlineList.vue";

// The outline as an in-page popover, for surfaces already inside a tab; the
// full-page builder keeps CustomFormOutline. `align` follows the button — a
// right-aligned one needs the panel to open leftward or it runs off the page.
withDefaults(
  defineProps<{ sections: OutlineSection[]; align?: "left" | "right" }>(),
  { align: "left" }
);

const emit = defineEmits<{
  "jump-section": [key: string | number];
  "jump-question": [key: string | number];
}>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);

function emitJump(
  event: "jump-section" | "jump-question",
  key: string | number
) {
  open.value = false;
  emit(event, key);
}

function onDocumentClick(event: MouseEvent) {
  if (!open.value) return;
  if (!root.value?.contains(event.target as Node)) open.value = false;
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") open.value = false;
}

onMounted(() => {
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
.cf-jump {
  position: relative;
}

.cf-jump__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgb(209 213 219);
  border-radius: 0.375rem;
  background: #ffffff;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
  white-space: nowrap;
}
.cf-jump__btn:hover {
  background: rgb(249 250 251);
}
.cf-jump__btn--open {
  border-color: #2563eb;
  color: #1d4ed8;
}
.cf-jump__caret {
  font-size: 0.625rem;
  color: #9ca3af;
}

.cf-jump__panel {
  position: absolute;
  z-index: 30;
  top: calc(100% + 0.375rem);
  left: 0;
  width: 20rem;
  max-height: 24rem;
  overflow-y: auto;
  padding: 0.5rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(226 232 240);
  background: #ffffff;
  box-shadow:
    0 20px 40px -12px rgba(15, 23, 42, 0.25),
    0 8px 16px -8px rgba(15, 23, 42, 0.12);
}

.cf-jump__panel--right {
  left: auto;
  right: 0;
}

.cf-jump-panel-enter-active,
.cf-jump-panel-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
  transform-origin: top left;
}
.cf-jump__panel--right.cf-jump-panel-enter-active,
.cf-jump__panel--right.cf-jump-panel-leave-active {
  transform-origin: top right;
}
.cf-jump-panel-enter-from,
.cf-jump-panel-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .cf-jump-panel-enter-active,
  .cf-jump-panel-leave-active {
    transition: none;
  }
}
</style>
